// Four lamps, always visible: is anything broken right now?
//
// Green means something real worked recently, not that a ping succeeded -- the
// board is fed by the traffic that already happens (see state/health.ts). A
// system nobody has used yet is grey and says so, because claiming health for an
// untested system is how a board stops being trusted.
//
// Colour is never the only signal: each lamp carries a shape and a title, and
// the strip names what is wrong in words when something is.
import { useHealth, useBridgeStatus } from "../bridge";
import { describe, SYSTEM_LABEL, troubled, worst, type Status, type System } from "../state/health";

/* A mark per status as well as a colour, so the board is readable without it:
   colour alone fails the DM running this on a projector, in daylight, or
   colour-blind. */
const MARK: Record<Status, string> = { ok: "●", flapping: "◑", down: "✕", unknown: "○" };

export function HealthLamps() {
  const health = useHealth();
  /* Subscribed so the connector lamp follows the handshake even when no request
     has been made yet. */
  useBridgeStatus();
  const bad = troubled(health);
  const overall = worst(health);

  return (
    <div className={`health-lamps health-${overall}`} role="status" aria-label="System health">
      {(Object.keys(SYSTEM_LABEL) as System[]).map((key) => {
        const h = health[key];
        return (
          <span key={key} className={`lamp lamp-${h.status}`} title={describe(h)}>
            <b aria-hidden="true">{MARK[h.status]}</b>
            <span className="lamp-name">{SYSTEM_LABEL[key]}</span>
            {/* Said in words for a screen reader, since the mark is decorative. */}
            <span className="sr-only">{describe(h)}</span>
          </span>
        );
      })}
      {/* THE ANSWER IN WORDS. A row of lamps still makes the DM interpret; this
          says the thing. Only the worst is named -- two failures at once are
          almost always one cause, and a list is a thing to read rather than act
          on. */}
      {bad.length > 0 && (
        <em className="health-say" title={bad.map((h) => describe(h)).join("\n")}>
          {SYSTEM_LABEL[bad[0].system]} {bad[0].status === "down" ? "is failing" : "was failing"}
          {bad.length > 1 ? ` (and ${bad.length - 1} more)` : ""}
        </em>
      )}
    </div>
  );
}
